import { type FC } from 'react'
import classnames from 'classnames'
import { observer } from 'mobx-react-lite'
import { Avatar, EnumTimeMode, EnumTimeStatus, Time, Typography } from 'shared/ui'
import { type Conversation } from 'entities/Conversation'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { ConversationMessage } from 'entities/Conversation/ui/ConversationCard/ui/ConversationMessage/ConversationMessage'
import { InboxTag } from 'entities/Inbox'
import { isOptOutCurrentInbox } from 'entities/Contacts'
import styles from './styles.module.scss'

type IConversationListProps = {
  item: Conversation
  active: boolean
  onChangeActive: (item: Conversation) => void
}

export const ConversationCard: FC<IConversationListProps> = observer(
  ({ item, active, onChangeActive }) => {
    const entities = useEntitiesContext()
    const { contactsStore, inboxesStore, numbersStore } = entities
    const contact = contactsStore.getItem(item.contact_id)
    const inbox = inboxesStore.getItem(item.inbox_id)

    if (!inbox) return null
    if (!contact) return null

    const number = inbox?.type === 'inbox' ? numbersStore.getItem(inbox?.numberId) : undefined
    const isOptOut = number?.isShortCode
      ? isOptOutCurrentInbox({
          conversation: item,
          contact: contact,
          entities: entities,
        })
      : contact?.isOptOut ||
        isOptOutCurrentInbox({
          conversation: item,
          contact: contact,
          entities: entities,
        })

    const renderTime = () => {
      if (item.last_inbound_at || item.lastMessageAtValueOf) {
        const is_need_response = item.is_need_response
        return (
          <Time
            date={
              inbox?.color_coding_enabled && is_need_response
                ? item.last_inbound_at
                : item.lastMessageAtValueOf
            }
            position={'relative'}
            mode={EnumTimeMode.Live}
            status={item.isUnread ? EnumTimeStatus.Unread : EnumTimeStatus.Read}
            color_coding_enabled={inbox?.color_coding_enabled}
            is_need_response={is_need_response}
          />
        )
      }

      return null
    }

    return (
      <div
        id={`conversation_${item.id}`}
        className={classnames(styles.wrap, {
          [styles.active]: active,
          [styles.isOptOut]: isOptOut,
        })}
        onClick={() => {
          onChangeActive(item)
        }}
      >
        <div className={styles.left}>
          <div className={styles.info}>
            <div className={styles.info__avatar}>
              <Avatar info={contact.avatarInfo} size={16} fontSize={10} />
            </div>
            <Typography
              tag={'div'}
              className={styles.info__name}
              variant={'body-md-medium'}
              ariaLabel={'name'}
            >
              {contact.name}
            </Typography>
          </div>

          <div className={styles.text}>
            <ConversationMessage item={item} className={styles.text__label} />
          </div>

          <div className={styles.inbox}>
            <InboxTag
              className={styles.inbox__custom}
              inbox={inbox}
              iconProps={{
                fontSize: 12,
              }}
            />
          </div>
        </div>
        <div className={styles.right}>{renderTime()}</div>
      </div>
    )
  }
)
